import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';

const Home = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen">
      {/* Full-width Hero Section with stat.jpg */}
      <section className="relative h-[600px] w-full overflow-hidden">
        {/* Background Image - Full width, no margins */}
        <img
          src="/images/stat.jpg"
          alt="Solar Energy Statistics"
          className="absolute inset-0 w-full h-full object-cover scale-100"
        />
        
        {/* Green-blue overlay for website theme consistency */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-600/50 to-blue-600/50"></div>
        
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        
        {/* Content overlay */}
        <div className="relative z-10 flex flex-col justify-center items-center h-full text-center text-white px-4">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-2xl">
            GEPredict
              </h1>
          <p className="text-lg md:text-xl mb-6 max-w-2xl drop-shadow-lg">
            Discover your prediction
          </p>
          
          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="text-center p-4 border border-white border-opacity-30 rounded-lg bg-white bg-opacity-10 backdrop-blur-sm">
              <div className="text-3xl md:text-4xl font-bold text-white mb-2">85%</div>
              <div className="text-sm md:text-base text-gray-200">Energy Saving</div>
                  </div>
            <div className="text-center p-4 border border-white border-opacity-30 rounded-lg bg-white bg-opacity-10 backdrop-blur-sm">
              <div className="text-3xl md:text-4xl font-bold text-white mb-2">100%</div>
              <div className="text-sm md:text-base text-gray-200">Renewable</div>
            </div>
            <div className="text-center p-4 border border-white border-opacity-30 rounded-lg bg-white bg-opacity-10 backdrop-blur-sm">
              <div className="text-3xl md:text-4xl font-bold text-white mb-2">24/7</div>
              <div className="text-sm md:text-base text-gray-200">Monitoring</div>
            </div>
            <div className="text-center p-4 border border-white border-opacity-30 rounded-lg bg-white bg-opacity-10 backdrop-blur-sm">
              <div className="text-3xl md:text-4xl font-bold text-white mb-2">95%</div>
              <div className="text-sm md:text-base text-gray-200">Accuracy</div>
            </div>
          </div>
        </div>
        

      </section>

      {/* Research Highlights Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Research Highlights
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Published in the prestigious Solar Energy journal, our research represents breakthrough innovations 
              in photovoltaic performance modeling for grid-connected PERC PV strings
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Innovations</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>In-depth investigation</strong> for modeling and forecasting grid-connected PERC PV string performance
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>New mathematical models</strong> for DM and BM shape parameters as functions of temperature and irradiance
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Novel analytical formulas</strong> relating DM shape parameters to key PV metrics
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Real-world validation</strong> using data from grid-connected PERC PV string in Benguerir, Morocco
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Strong agreement</strong> between predicted and measured PV performance demonstrated
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl border border-green-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Technical Breakthrough</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Novel analytical expressions</strong> for PV module output current in single and multi-diode models
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Revolutionary approach</strong> for extracting multi-diode model physical parameters
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Taylor Series Expressions (TSEC)</strong> enabling efficient parameter search and accurate I-V data generation
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Benchmark validation</strong> against exact numerical solutions with superior precision
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            Experience Advanced Solar Modeling
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            Discover how our research-backed models can enhance your grid-connected PV string performance forecasting
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to={isAuthenticated ? "/predict" : "/login"}
              className="inline-flex items-center px-10 py-4 bg-gradient-to-r from-blue-500 to-green-500 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-green-600 transition-all duration-200 shadow-lg text-lg"
            >
              Start Prediction Tool
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center px-10 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200 text-lg"
            >
              Read Full Research
            </Link>
          </div>
        </div>
      </section>


    </div>
  );
};

export default Home;
